import { useEffect, useState, useContext } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftEditor from "components/SoftEditor";
import { useAuth0 } from "@auth0/auth0-react";
import { PERMISSIONS } from "constants/portal";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { updateRoles } from "api/RolesApi";
import { fetchRoles } from "redux/actions/portal/rolesActionCreator";

function hasContent(string) {
  const textOnly = string.replace(/<[^>]*>/g, "").trim();
  return textOnly.length > 0;
}

const Overview = ({ selectedRole, isReadOnlyAccess }) => {
  const [documentation, setDocumentation] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useAuth0();
  const dispatch = useDispatch();
  const { userPermissions } = useContext(PermissionsContext);

  const isAllowedAction = (action) => {
    if (userPermissions.some((permission) => permission.name === "owner" && permission.value)) {
      return true;
    }

    switch (action) {
      case "delete":
        return userPermissions.some(
          (permission) => permission.name === PERMISSIONS.DELETE_ROLES && permission.value
        );
      case "remove":
        return userPermissions.some(
          (permission) => permission.name === PERMISSIONS.REMOVE_USERS && permission.value
        );
      case "edit":
        return userPermissions.some(
          (permission) => permission.name === PERMISSIONS.EDIT_ROLES && permission.value
        );
      default:
        return false;
    }
  };

  const handleCancelEdit = () => setIsEditing(false);

  const handleEditDocumentation = () => setIsEditing(true);

  const handleSaveDocumentation = async () => {
    const validRolesInputFields = [
      "notes",
      "created_by",
      "description",
      "name",
      "status",
      "user_type",
      "created_by_role_id",
    ];

    const input = {};

    for (const key of validRolesInputFields) {
      if (selectedRole && Object.prototype.hasOwnProperty.call(selectedRole, key)) {
        if (selectedRole[key] !== null && selectedRole[key] !== undefined) {
          input[key] = selectedRole[key];
        } else if (key === "created_by_role_id" && selectedRole[key] === null) {
          input[key] = null;
        }
      }
    }

    input.notes = hasContent(documentation) ? documentation : "";

    try {
      const response = await updateRoles(user?.stripeId, selectedRole?.id, input);

      if (response?.isSuccessful) {
        toast.success("Documentation saved");
        setIsEditing(false);

        dispatch(fetchRoles(user?.stripeId));
      } else {
        toast.error(response?.message || "Failed to save documentation");
      }
    } catch (error) {
      console.error("Error saving role documentation:", error);
      toast.error("An error occurred while saving documentation");
    }
  };

  const handleOnMouseEnter = () => {
    document.body.style.overflow = "hidden";
  };

  const handleOnMouseLeave = () => {
    document.body.style.overflow = "visible";
  };

  useEffect(() => {
    if (selectedRole) {
      setDocumentation(selectedRole.notes || "");
    } else {
      setDocumentation("");
    }
  }, [selectedRole]); 

  useEffect(() => {
    if (!isEditing) {
      document.body.style.overflow = "visible";
    }
  }, [isEditing]);

  if (!selectedRole) return <></>;

  return (
    <SoftBox className="overview">
      <SoftBox
        sx={{
          marginBottom: "24px",
          padding: "20px 24px 0 0",
        }}
      >
      {isAllowedAction("edit") && (
        <SoftButton
          sx={{
            display: "block !important",
            marginLeft: "auto !important",
          }}
          variant="outlined"
          color="info"
          onClick={handleEditDocumentation}
          disabled={isReadOnlyAccess}
        >
          edit
        </SoftButton>
      )}
      </SoftBox>
      <SoftBox
        className="border-top"
        sx={{
          padding: "32px 12px",
        }}
      >
        {!isEditing && (
          <SoftBox
            sx={{
              textAlign: "center",
              padding: `${documentation ? "" : "48px"} 0 ${documentation ? "" : "64px"}`,
            }}
          >
            {!documentation && <SoftTypography fontSize="16px">Add documentation</SoftTypography>}
            <SoftEditor
              className="documentation preview"
              theme="bubble"
              value={documentation}
              readOnly
            />
          </SoftBox>
        )}
        {isEditing && (
          <SoftBox onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
            <SoftEditor
              theme="snow"
              value={documentation}
              onChange={(value) => setDocumentation(value)}
            />
            <SoftBox
              sx={{
                paddingTop: "24px",
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
                columnGap: "18px",
              }}
            >
              <SoftButton
                sx={{
                  minWidth: "110px",
                }}
                variant="outlined"
                color="info"
                onClick={handleCancelEdit}
              >
                cancel
              </SoftButton>
              <SoftButton
                sx={{
                  minWidth: "110px",
                }}
                variant="gradient"
                size="medium"
                color="primary"
                onClick={handleSaveDocumentation}
                disabled={!documentation}
              >
                save
              </SoftButton>
            </SoftBox>
          </SoftBox>
        )}
      </SoftBox>
    </SoftBox>
  );
};

export default Overview;
